import type { FC } from "react";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Checkbox,
  Dropdown,
  IDropdownOption,
  PrimaryButton,
  DefaultButton,
  Stack,
  ChoiceGroup,
  DatePicker,
  DayOfWeek,
  IChoiceGroupOption,
  IconButton,
  Label,
} from "@fluentui/react";
import { ArrowSwap24Filled } from "@fluentui/react-icons";
import { Input, Spinner, useToastController } from "@fluentui/react-components";

import {
  AdminService,
  CouponDto as CouponApiDto,
  Modul,
} from "../../KulcsUzletSvc";
import { ToasterContext } from "../../utils/GlobalContexts";
import { msalInstance } from "../../utils/auth/AuthProvider";
import { notify } from "../../utils/DispatchToaster";
import { TOAST } from "../../utils/Enums";
import {
  formatTaxNumberInput,
  isFormattedTaxNumberValid,
} from "../../utils/taxNumber";

enum CouponType {
  All = "All",
  OnlyBase = "OnlyBase",
  OnlyModul = "OnlyModul",
  SpecifyModul = "SpecifyModul",
  OnlyBaseOnlyOneUser = "OnlyBaseOnlyOneUser",
}
const BASE_PRICE = 3810;
const couponTypeLabels: Record<CouponType, string> = {
  [CouponType.All]: "Mindenre",
  [CouponType.OnlyBase]: "Csak alapmodulra",
  [CouponType.OnlyModul]: "Csak kiegészítő modulokra",
  [CouponType.SpecifyModul]: "Csak modulra",
  [CouponType.OnlyBaseOnlyOneUser]:
    "Egyszer használható kupon, csak alapmodulra",
};
const couponTypes: IDropdownOption[] = Object.values(CouponType).map(
  (type) => ({
    key: type,
    text: couponTypeLabels[type],
  }),
);
// modul lista a backendből (Admin/moduls), a modulId mezőhöz valós GUID kell
const mapModulToOption = (m: Modul): IDropdownOption => ({
  key: m.id ?? "",
  text: `${m.name} - ${
    m.monthPrice === 0 ? "ingyenes" : m.monthPrice + " Ft/hó"
  }`,
});

const generateCode = () => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let result = "";
  for (let i = 0; i < 11; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

interface CouponFormState {
  merchant: string;
  code: string;
  type: CouponType;
  module: string;
  discount: number;
  price: number;
  redeemableUntil: Date | null;
  validityDays: number | string;
  validityDate: Date | null;
  isFixedAmount: boolean;
  discountAmount: number | string;
  taxNumber: string;
}

const getInitialState = (traderName: string): CouponFormState => ({
  merchant: traderName,
  code: generateCode(),
  type: CouponType.All,
  module: "",
  discount: 10,
  price: Math.round(BASE_PRICE * 0.9),
  validityDays: 365,
  validityDate: null,
  redeemableUntil: null,
  isFixedAmount: false,
  discountAmount: "",
  taxNumber: "",
});

const mapApiToFormState = (
  apiData: any,
  merchantName: string,
): CouponFormState => {
  const fixedDiscountValue =
    (apiData as any)?.discountValue ?? (apiData as any)?.DiscountValue;
  const isFixed =
    fixedDiscountValue !== undefined && fixedDiscountValue !== null;
  const taxFromApi = String(
    (apiData as any)?.taxNumber ?? (apiData as any)?.taxnumber ?? "",
  );

  const discount = Number(apiData?.discount ?? 0);
  const price = Math.round(BASE_PRICE * (1 - discount / 100));
  const mapCouponTypeFromApi = (t: unknown): CouponType => {
    if (typeof t === "string") {
      switch (t) {
        case "OnlyBase":
          return CouponType.OnlyBase;
        case "OnlyModul":
          return CouponType.OnlyModul;
        case "SpecifyModul":
          return CouponType.SpecifyModul;
        case "OnlyBaseOnlyOneUser":
          return CouponType.OnlyBaseOnlyOneUser;
        case "All":
        default:
          return CouponType.All;
      }
    }

    const n = typeof t === "number" ? t : Number(t);
    switch (n) {
      case 1:
        return CouponType.OnlyBase;
      case 2:
        return CouponType.OnlyModul;
      case 3:
        return CouponType.SpecifyModul;
      case 4:
        return CouponType.OnlyBaseOnlyOneUser;
      case 0:
      default:
        return CouponType.All;
    }
  };

  const validDate = apiData?.validTo ? new Date(apiData.validTo) : null;

  return {
    merchant: merchantName,
    code: apiData?.couponCode ?? "",
    type: mapCouponTypeFromApi(apiData?.couponType),
    module: apiData?.modulId ?? "",
    discount: discount,
    price: price,

    redeemableUntil: validDate,

    validityDays: apiData?.validDays ?? "",
    validityDate: validDate,

    isFixedAmount: isFixed,
    discountAmount: isFixed ? fixedDiscountValue : "",
    taxNumber: isFixed ? formatTaxNumberInput(taxFromApi) : "",
  };
};

export const CouponEditForm: FC = () => {
  const { traderId, id: couponId } = useParams<{
    traderId: string;
    id: string;
  }>();

  const navigate = useNavigate();
  const { toasterId } = useContext(ToasterContext);
  const { dispatchToast } = useToastController(toasterId);

  const [resolvedTraderId, setResolvedTraderId] = useState<string | null>(
    traderId || null,
  );

  const [isDataLoading, setIsDataLoading] = useState(true);
  const [formData, setFormData] = useState<CouponFormState>(
    getInitialState(""),
  );

  const initialMode =
    formData.validityDays && Number(formData.validityDays) > 0
      ? "days"
      : "date";
  const [validityMode, setValidityMode] = useState<"days" | "date">(
    initialMode,
  );

  const expiryOptions: IChoiceGroupOption[] = [
    { key: "days", text: "Beváltás után x napig érvényes" },
    { key: "date", text: "Konkrét dátumig érvényes" },
  ];

  const [moduleOptions, setModuleOptions] = useState<IDropdownOption[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const res = await AdminService.getAdminModuls();
        // Webshop modul nem választható kuponhoz
        setModuleOptions(
          (res ?? [])
            .filter((m) => m.name !== "Kulcs Flow Webshop")
            .map(mapModulToOption),
        );
      } catch (e) {
        console.log("Nem sikerült betölteni a modulokat:", e);
      }
    };
    fetchModules();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsDataLoading(true);

      let currentTraderId = traderId;
      let couponRes: any | null = null;

      try {
        if (couponId) {
          const raw = (await AdminService.getAdminCoupon(couponId)) as any;
          couponRes = raw?.resultValue ?? raw;

          if (!couponRes) {
            notify(
              TOAST.WARN,
              `A kupon (${couponId}) nem található.`,
              dispatchToast,
            );
            navigate(
              currentTraderId
                ? `/admin/trader/${currentTraderId}/coupons`
                : `/admin/coupons`,
            );
            return;
          }

          if (!currentTraderId && couponRes?.traderId) {
            currentTraderId = couponRes.traderId;
          }
        }

        if (!currentTraderId) {
          console.error("Hiányzik a traderId. A mentés nem lesz lehetséges.");
          notify(
            TOAST.ERROR,
            "Hiányzó kereskedő azonosító a kuponhoz.",
            dispatchToast,
          );
          navigate("/admin/coupons");
          return;
        }

        setResolvedTraderId(currentTraderId);

        let merchantName = "";
        try {
          const res =
            await AdminService.getAdminApplicationUser(currentTraderId);
          merchantName = res?.fullName ?? "";
        } catch (e) {
          console.log("Nem található a kereskedő:", e);
        }

        if (couponRes) {
          const loadedState = mapApiToFormState(couponRes, merchantName);
          const isDaysMode = Number(couponRes?.validDays) > 0;
          setValidityMode(isDaysMode ? "days" : "date");
          setFormData(loadedState);
        } else {
          setFormData(getInitialState(merchantName));
        }
      } catch (e) {
        console.error("Kupon/kereskedő betöltési hiba:", e);
        notify(TOAST.ERROR, `Hiba a betöltéskor: ${e}`, dispatchToast);
        navigate("/admin/coupons");
      } finally {
        setIsDataLoading(false);
      }
    };
    fetchData();
  }, [traderId, couponId, navigate, dispatchToast]);

  const handleChange = (
    field: keyof CouponFormState,
    value: string | number | boolean | Date | null,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDiscountChange = (value: string) => {
    let discount = parseFloat(value) || 0;
    if (discount < 1) discount = 1;
    if (discount > 99) discount = 99;
    const newPrice = Math.round(BASE_PRICE * (1 - discount / 100));
    setFormData((prev) => ({ ...prev, discount, price: newPrice }));
  };

  const handlePriceChange = (value: string) => {
    const price = parseFloat(value) || 0;
    const discount = Math.round(((BASE_PRICE - price) / BASE_PRICE) * 100);
    setFormData((prev) => ({ ...prev, price, discount }));
  };

  const handleCancel = () => {
    const targetId = resolvedTraderId || traderId;
    if (targetId) {
      navigate(`/admin/trader/${targetId}/coupons`);
    } else {
      navigate(`/admin/coupons`);
    }
  };

  const handleSubmit = () => {
    (async () => {
      const currentTraderId = resolvedTraderId;
      if (!currentTraderId) {
        notify(
          TOAST.ERROR,
          "A kereskedő azonosító hiányzik, a mentés nem lehetséges.",
          dispatchToast,
        );
        return;
      }

      setIsSaving(true);
      try {
        let validatedTaxNumber: string | undefined;

        if (!formData.code) {
          alert("Kérem adjon meg egy kuponkódot!");
          return;
        }

        if (formData.isFixedAmount) {
          const amount = Number(formData.discountAmount);
          if (!amount || amount <= 0) {
            alert("Kérem adjon meg egy kedvezmény összeget!");
            return;
          }
          if (!formData.redeemableUntil) {
            alert("Kérem adja meg, meddig érvényesíthető a kupon!");
            return;
          }

          const rawTax = String(formData.taxNumber || "").trim();
          if (rawTax) {
            const formattedTax = formatTaxNumberInput(rawTax);

            if (!isFormattedTaxNumberValid(formattedTax)) {
              alert("Az adószám formátuma hibás. Várt: 12345678-1-12");
              return;
            }

            validatedTaxNumber = formattedTax;
          }
        }

        const mapCouponType = (t: CouponType) => {
          switch (t) {
            case CouponType.OnlyBase:
              return 1;
            case CouponType.OnlyModul:
              return 2;
            case CouponType.SpecifyModul:
              return 3;
            case CouponType.OnlyBaseOnlyOneUser:
              return 4;
            case CouponType.All:
            default:
              return 0;
          }
        };

        const isGuid = (v: string) =>
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
            v,
          );

        const resolveApplicationUserId = async (): Promise<string | null> => {
          try {
            const account =
              msalInstance.getActiveAccount() ||
              (msalInstance.getAllAccounts && msalInstance.getAllAccounts()[0]);
            if (!account) return null;
            const claims = (account as any).idTokenClaims || {};
            const candidates: Array<string | undefined> = [
              (account as any).username,
              (account as any).name,
              claims.preferred_username,
              claims.upn,
              claims.email,
              (account as any).homeAccountId,
            ];

            for (const c of candidates) {
              if (!c) continue;
              try {
                const res = await AdminService.getAdminApplicationUsers(
                  c,
                  undefined,
                  undefined,
                  1,
                );
                const found =
                  res?.items && res.items.length > 0 ? res.items[0] : null;
                if (found && found.id) return found.id;
              } catch (_e) {
                // ignore and try next candidate
              }
            }

            return null;
          } catch (e) {
            console.log("Failed to resolve ApplicationUser id:", e);
            return null;
          }
        };

        const appUserId = await resolveApplicationUserId();
        const resolvedCreatorId = appUserId ?? currentTraderId;

        const payload: Partial<CouponApiDto> & {
          discountValue?: number;
          taxNumber?: string;
          creatorId?: string;
          modifierId?: string;
        } = {
          id: couponId || undefined,
          couponCode: formData.code,
          couponType: mapCouponType(formData.type) as any,
          modulId: isGuid(formData.module) ? formData.module : null,
          discount: formData.isFixedAmount
            ? undefined
            : Number(formData.discount),
          traderId: currentTraderId,
          validDays: 0,
          validTo: undefined,
        };

        if (formData.isFixedAmount) {
          const amount = Number(formData.discountAmount);
          payload.discountValue = amount;
        }

        if (formData.isFixedAmount && validatedTaxNumber) {
          payload.taxNumber = validatedTaxNumber;
        }

        // Fix összegű mód: csak a "Meddig érvényesíthető" dátumot használjuk,
        // és nem mutatjuk/használjuk a régi "validDays/validityDate" logikát.
        if (formData.isFixedAmount) {
          payload.validDays = 0;
          if (formData.redeemableUntil) {
            payload.validTo = formData.redeemableUntil.toISOString();
            payload.expiredDate = formData.redeemableUntil.toISOString();
          }
        } else {
          // Beváltási határidő (ha van)
          if (formData.redeemableUntil) {
            payload.validTo = formData.redeemableUntil.toISOString();
          }

          // Érvényesség beállítása
          if (validityMode === "date" && formData.validityDate) {
            payload.validTo = formData.validityDate.toISOString();
            payload.expiredDate = formData.validityDate.toISOString();
            payload.validDays = 0;
          } else if (validityMode === "days") {
            const vd = Number(formData.validityDays) || 0;
            payload.validDays = vd;
            const now = new Date();
            const expiry = new Date(now.getTime() + vd * 24 * 60 * 60 * 1000);
            payload.validTo = expiry.toISOString();
            payload.expiredDate = expiry.toISOString();
          }
        }

        if (resolvedCreatorId) {
          payload.creatorId = resolvedCreatorId;
          payload.modifierId = resolvedCreatorId;
        }

        const res = await AdminService.postAdminCoupon(payload as any);

        if (res?.isSuccess) {
          notify(
            TOAST.SUCCESS,
            `${couponId ? "Kupon módosítva" : "Új kupon létrehozva"}!`,
            dispatchToast,
          );
          //A feloldott ID-t használjuk navigációhoz
          navigate(`/admin/trader/${currentTraderId}/coupons`);
        } else {
          // üzleti validáció (pl. duplikált kód) - figyelmeztetés, nem hiba
          notify(
            TOAST.WARN,
            res?.errorMessage || "A kupon mentése nem sikerült.",
            dispatchToast,
          );
        }
      } catch (e) {
        console.error(e);
        notify(
          TOAST.ERROR,
          `Hiba történt a kupon mentése közben.`,
          dispatchToast,
        );
      } finally {
        setIsSaving(false);
      }
    })();
  };

  const isModuleDisabled = formData.type !== CouponType.SpecifyModul;

  if (isDataLoading) {
    return (
      <div style={{ textAlign: "center", marginTop: 40 }}>
        <Spinner size="large" label="Adatok betöltése..." />
      </div>
    );
  }

  return (
    <Stack
      horizontalAlign="center"
      verticalAlign="start"
      styles={{ root: { width: "100%", marginTop: 40 } }}
    >
      <Stack
        styles={{
          root: {
            width: 600,
            border: "1px solid #ddd",
            padding: 24,
            borderRadius: 4,
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
          },
        }}
        tokens={{ childrenGap: 16 }}
      >
        <h2 style={{ textAlign: "center" }}>
          {couponId ? "Kupon módosítása" : "Új kupon létrehozása"}
        </h2>

        <Label>Kereskedő neve</Label>
        <Input value={formData.merchant} readOnly />

        <Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign="end">
          <Stack.Item grow>
            <Label>Kupon kód</Label>
            <Input
              value={formData.code}
              readOnly={!!couponId}
              onChange={(_, data) => handleChange("code", data.value)}
            />
          </Stack.Item>
          {!couponId && (
            <IconButton
              title="Kód generálása"
              ariaLabel="Kód generálása"
              onRenderIcon={() => (
                <ArrowSwap24Filled style={{ fontSize: 20 }} />
              )}
              onClick={() => setFormData({ ...formData, code: generateCode() })}
            />
          )}
        </Stack>

        <Dropdown
          label="Kupon típusa"
          selectedKey={formData.type}
          options={couponTypes}
          onChange={(_, option) => {
            if (!option) return;
            handleChange("type", option.key as CouponType);
          }}
        />

        <Dropdown
          label="Modul"
          selectedKey={formData.module}
          options={moduleOptions}
          disabled={isModuleDisabled}
          onChange={(_, option) =>
            handleChange("module", String(option?.key ?? ""))
          }
        />

        <Checkbox
          label="Fix összegű"
          checked={formData.isFixedAmount}
          onChange={(_, checked) => handleChange("isFixedAmount", !!checked)}
        />

        {formData.isFixedAmount ? (
          <>
            <Label>Kedvezmény összege (Ft)</Label>
            <Input
              type="number"
              value={String(formData.discountAmount ?? "")}
              onChange={(_, data) => handleChange("discountAmount", data.value)}
            />

            <Label>Adószám (opcionális)</Label>
            <Input
              type="text"
              value={formData.taxNumber}
              onChange={(_, data) =>
                handleChange("taxNumber", formatTaxNumberInput(data.value))
              }
            />
          </>
        ) : (
          <>
            <Label>Kedvezmény (%)</Label>
            <Input
              min={1}
              max={99}
              type="number"
              value={formData.discount.toString()}
              onChange={(_, data) => handleDiscountChange(data.value)}
            />

            <Label>{`Alap modul ára ${BASE_PRICE} Ft helyett havonta (Ft)`}</Label>
            <Input
              type="number"
              value={formData.price.toString()}
              onChange={(_, data) => handlePriceChange(data.value)}
            />
          </>
        )}

        <DatePicker
          label="Meddig érvényesíthető"
          firstDayOfWeek={DayOfWeek.Monday}
          value={formData.redeemableUntil || undefined}
          onSelectDate={(date) => handleChange("redeemableUntil", date || null)}
          placeholder="Válassz dátumot"
        />

        {!formData.isFixedAmount && (
          <>
            <ChoiceGroup
              selectedKey={validityMode}
              options={expiryOptions}
              onChange={(_, option) =>
                setValidityMode(option?.key as "days" | "date")
              }
            />

            {validityMode === "days" && (
              <>
                <Label>Meddig érvényes (nap)</Label>
                <Input
                  type="number"
                  value={formData.validityDays.toString()}
                  onChange={(_, data) =>
                    handleChange("validityDays", data.value)
                  }
                />
              </>
            )}

            {validityMode === "date" && (
              <DatePicker
                label="Meddig érvényes (dátum)"
                firstDayOfWeek={DayOfWeek.Monday}
                value={formData.validityDate || undefined}
                onSelectDate={(date) =>
                  handleChange("validityDate", date || null)
                }
                placeholder="Válassz dátumot"
              />
            )}
          </>
        )}

        <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 10 }}>
          <DefaultButton text="Mégse" onClick={handleCancel} />
          <PrimaryButton
            text={isSaving ? "Mentés..." : "Mentés"}
            onClick={handleSubmit}
            disabled={isSaving}
            onRenderIcon={isSaving ? () => <Spinner size="tiny" /> : undefined}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default CouponEditForm;
